// Ported from invenire-v2 src/hooks/auth/idle-timeout.ts and activity.ts (framework-free, so it runs anywhere)
import { IDLE_TIME } from '../_config/config';

export const IDLE_TIMEOUT_MS = IDLE_TIME * 1000;
export const SESSION_WARNING_MS = 60 * 1000;
/** Activity refreshes the access token once it is this old. */
export const TOKEN_REFRESH_AFTER_MS = 60 * 1000;

/** What counts as someone using the page, for both the idle timer and the token refresh. */
export const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

type ActivityTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

/** Calls onSettled `delayMs` after the last activity event. Returns a function that stops watching. */
export const watchActivity = ({
  target,
  onSettled,
  delayMs,
  events = ACTIVITY_EVENTS,
}: {
  target: ActivityTarget;
  onSettled: () => void;
  delayMs: number;
  events?: string[];
}) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  const handleActivity = () => {
    clearTimeout(timeout);
    timeout = setTimeout(onSettled, delayMs);
  };

  events.forEach((event) => target.addEventListener(event, handleActivity));

  return () => {
    clearTimeout(timeout);
    events.forEach((event) => target.removeEventListener(event, handleActivity));
  };
};

/** True once the access token (its `iat` claim, in seconds) is a minute old. */
export const shouldRefreshToken = (token: { iat?: number } | null, now = Date.now()) =>
  !!token?.iat && now - token.iat * 1000 >= TOKEN_REFRESH_AFTER_MS;

/** Signs out after `timeoutMs` without activity, warning `warningMs` ahead. */
export const createIdleTimeoutManager = ({
  target,
  onIdle,
  onWarning,
  onWarningCleared,
  timeoutMs = IDLE_TIMEOUT_MS,
  warningMs = SESSION_WARNING_MS,
  events = ACTIVITY_EVENTS,
}: {
  target: ActivityTarget;
  onIdle: () => void;
  /** Called with the ms left until the sign-out */
  onWarning?: (msLeft: number) => void;
  onWarningCleared?: () => void;
  timeoutMs?: number;
  warningMs?: number;
  events?: string[];
}) => {
  let signOutTimeout: ReturnType<typeof setTimeout> | undefined;
  let warningTimeout: ReturnType<typeof setTimeout> | undefined;
  let warned = false;

  const clearWarning = () => {
    if (!warned) { return; }
    warned = false;
    onWarningCleared?.();
  };

  const stop = () => {
    clearTimeout(signOutTimeout);
    clearTimeout(warningTimeout);
    clearWarning();
    events.forEach((event) => target.removeEventListener(event, handleActivity));
  };

  const handleActivity = () => {
    clearTimeout(signOutTimeout);
    clearTimeout(warningTimeout);
    clearWarning();

    signOutTimeout = setTimeout(() => {
      // Sign out once; activity during the logout request mustn't sign out again.
      stop();
      onIdle();
    }, timeoutMs);

    warningTimeout = setTimeout(() => {
      warned = true;
      onWarning?.(warningMs);
    }, timeoutMs - warningMs);
  };

  return {
    start: () => {
      events.forEach((event) => target.addEventListener(event, handleActivity));
      handleActivity();
    },
    stop,
  };
};

/** The countdown toast's text; InvUI re-renders it every second, so it counts seconds. */
export const sessionWarningMessage = (msLeft: number) => {
  const seconds = Math.max(Math.ceil(msLeft / 1000), 1);
  return `You'll be signed out in ${seconds} ${seconds === 1 ? 'second' : 'seconds'} for inactivity. ` +
    'Move the mouse or press a key to stay signed in.';
};
